// src/homepage/Reviews.tsx
// Homepage social-proof section: the newest reviews across both trips
// (photos shown when a review has them) scraped from GetYourGuide.
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { austriaReviews, slovakiaReviews } from '../constants/reviews';

const GYG_BUSINESS_URL = 'https://www.getyourguide.com/drl-business-s596781/';

const Section = styled.section`
    padding: 80px 0;
    background: #f8fafc;
`;

const Inner = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
`;

const SectionTitle = styled(motion.h2)`
    font-size: 3rem;
    font-weight: 800;
    text-align: center;
    margin-bottom: 1rem;
    color: #1a1a1a;
    line-height: 1.2;

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }
`;

const SummaryRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
    margin-bottom: 3rem;
    color: #6b7280;
    font-size: 1.05rem;
`;

const RatingPill = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    padding: 0.4rem 0.9rem;
    font-weight: 700;
    color: #1a1a1a;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);

    span.stars {
        color: #fbbf24;
        letter-spacing: 1px;
    }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
    gap: 1.5rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const Card = styled(motion.div)`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
`;

const Author = styled.div`
    display: flex;
    flex-direction: column;
`;

const Avatar = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: #fff;
    font-weight: 700;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const AuthorRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;

const Name = styled.span`
    font-weight: 700;
    color: #1a1a1a;
`;

const Meta = styled.span`
    font-size: 0.85rem;
    color: #9ca3af;
`;

const Stars = styled.div`
    color: #fbbf24;
    font-size: 1rem;
    letter-spacing: 1px;
    white-space: nowrap;
`;

const Content = styled.p`
    color: #4b5563;
    line-height: 1.6;
    font-size: 0.95rem;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const Photos = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const Photo = styled.img`
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
`;

const VerifiedTag = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.8rem;
    color: #059669;
    font-weight: 600;
`;

const ViewAll = styled.div`
    text-align: center;
    margin-top: 2.5rem;
`;

const ViewAllLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: #fff;
    text-decoration: none;
    padding: 0.85rem 1.75rem;
    border-radius: 10px;
    font-weight: 600;
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    }
`;

const Reviews: React.FC = () => {
    const all = [...austriaReviews, ...slovakiaReviews];
    const total = all.length;
    const avg = total ? all.reduce((sum, r) => sum + r.rating, 0) / total : 5;
    const avgLabel = avg.toFixed(1);

    // Newest reviews across both trips (photos optional) so the feed shows we're
    // still running tours, not just past ones.
    const newestReviews = [...all]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 9);

    return (
        <Section id="reviews">
            <Inner>
                <SectionTitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    Loved by Travelers
                </SectionTitle>
                <SummaryRow>
                    <RatingPill>
                        <span className="stars">★★★★★</span>
                        {avgLabel} / 5
                    </RatingPill>
                    <span>from {total}+ verified reviews on GetYourGuide</span>
                </SummaryRow>

                <Grid>
                    {newestReviews.map((review, index) => {
                        const initial = review.name.trim().charAt(0).toUpperCase() || '★';
                        return (
                            <Card
                                key={review.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <CardHeader>
                                    <AuthorRow>
                                        <Avatar>{initial}</Avatar>
                                        <Author>
                                            <Name>{review.name}</Name>
                                            <Meta>{review.location} • {review.date}</Meta>
                                        </Author>
                                    </AuthorRow>
                                    <Stars>{'★'.repeat(review.rating)}</Stars>
                                </CardHeader>

                                <Content>{review.content}</Content>

                                {review.images && review.images.length > 0 && (
                                    <Photos>
                                        {review.images.slice(0, 3).map((src, i) => (
                                            <Photo key={i} src={src} alt={`${review.name}'s photo ${i + 1}`} loading="lazy" />
                                        ))}
                                    </Photos>
                                )}

                                {review.verified && <VerifiedTag>✓ Verified booking</VerifiedTag>}
                            </Card>
                        );
                    })}
                </Grid>

                <ViewAll>
                    <ViewAllLink href={GYG_BUSINESS_URL} target="_blank" rel="noopener noreferrer">
                        Read all reviews on GetYourGuide →
                    </ViewAllLink>
                </ViewAll>
            </Inner>
        </Section>
    );
};

export default Reviews;
